import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import {
  gray85,
  IconCheckAlt,
  IconRefresh,
  IconWithText,
  IconXAlt,
  LoadingIndicator,
  spacer12,
  spacer24,
  spacer8,
  TableCell,
  TableRow,
  TextLink,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { DeliveryStatus } from '@hooks/types';
import {
  AssetTranscodingStatus,
  AssetType,
  DeliveryError,
  TranscodingStatus,
} from '@hooks/useAlbumInfo/types';
import { useProductXml } from '@hooks/useProductXml';
import { useOverrideAppContext } from '@context/OverrideAppContext';
import { CopyToGreenCheckAnimation } from '@components/shared/CopyToGreenCheckAnimation';
import { StyledDownloadIcon } from '@components/shared/DownloadIcon';
import { PillInfo } from '@components/shared/PillInfo/PillInfo';
import { PillInfoWithIcon } from '@components/shared/PillInfo/PillInfoWithIcon';
import { Column } from '@components/shared/styles';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { DeliveryErrors } from './DeliveryErrors';
import DeliveryXmlContent from './DeliveryXmlContent/DeliveryXmlContent';

const Container = styled.div`
  padding: ${spacer12} ${spacer24} ${spacer12};
`;

const StyledTableCell = styled(TableCell)`
  box-shadow: 0 4px 8px 0 ${gray85};
`;

const PillsContainer = styled.div`
  display: flex;
`;

const StyledTextLink = styled(TextLink)`
  text-decoration: none !important;
  cursor: pointer;

  &:hover {
    text-decoration: underline !important;
  }
`;

const StyledIconText = styled(Type)`
  margin-left: -${spacer8};
`;

const PillInfoCaseWrapper = styled.span`
  span {
    text-transform: initial;
  }
`;

export const CopyIconContainer = styled.div`
  margin-right: ${spacer8};
`;

export enum DOWNLOAD_STATUS {
  IDLE = 'Idle',
  DOWNLOADING = 'Downloading',
  SUCCESS = 'Success',
  ERROR = 'Error',
}

interface TableRowExpandedProps {
  deliveryId: string;
  deliveryStatus: DeliveryStatus;
  feedGid: string;
  productId: string;
  deliveryErrors: DeliveryError[];
  assetTranscodingStatuses: AssetTranscodingStatus[];
}

export const TableRowExpanded = ({
  deliveryId,
  deliveryStatus,
  feedGid,
  productId,
  deliveryErrors,
  assetTranscodingStatuses,
}: TableRowExpandedProps) => {
  const [downloadStatus, setDownloadStatus] = useState<DOWNLOAD_STATUS>(
    DOWNLOAD_STATUS.IDLE,
  );

  const t = useT();
  const router = useRouter();
  const { id: albumUri } = router.query;
  const {
    data: xmlData,
    status: xmlDataStatus,
    actions: { getXmlData },
    mutate,
  } = useProductXml(
    typeof albumUri === 'string' ? albumUri : '',
    feedGid,
    deliveryId,
    productId,
  );
  const { getSonarUrlFromId } = useOverrideAppContext();

  const downloadXMLFile = (blob: Blob, filename: string) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadStatus(DOWNLOAD_STATUS.SUCCESS);
    } catch {
      sendEvent({
        eventCategory: EVENT_CATEGORY.RELEASE_DELIVERIES_SECTION,
        eventAction: EVENT_ACTION.CLICK,
        eventLabel: 'Delivery XML file download failed',
        product: GA_PRODUCT_CATALOG,
      });
      // @TO-DO: Add error state when Design aligns on S4MP download error patterns
      setDownloadStatus(DOWNLOAD_STATUS.IDLE);
    }
  };

  useEffect(() => {
    // When there is an error fetching xml data
    if (xmlData !== undefined && xmlDataStatus !== 200) {
      // @TO-DO: Add error state when Design aligns on S4MP download error patterns
      setDownloadStatus(DOWNLOAD_STATUS.IDLE);
    }
    // When there is xml data available, download file
    if (xmlData?.data || xmlData?.fileName) {
      downloadXMLFile(new Blob([xmlData.data]), xmlData.fileName);
    }
  }, [xmlData, xmlDataStatus]);

  const handleDownloadIconClick = () => {
    setDownloadStatus(DOWNLOAD_STATUS.DOWNLOADING);
    sendEvent({
      eventCategory: EVENT_CATEGORY.RELEASE_DELIVERIES_SECTION,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Delivery XML file downloaded',
      product: GA_PRODUCT_CATALOG,
    });

    // When there is xml data available, download file
    if (xmlData?.data || xmlData?.fileName) {
      downloadXMLFile(new Blob([xmlData.data]), xmlData.fileName);
    }

    // When xml data was never fetched before
    if (xmlData === undefined) {
      getXmlData(deliveryId);
    }

    // When xml data was fetched before, but wasn't available (i.e error fetching)
    if (xmlData === null) {
      mutate();
    }
  };

  const renderTranscodingStatusValue = (
    transcodingStatus: TranscodingStatus,
  ) => {
    switch (transcodingStatus) {
      case TranscodingStatus.STATUS_SUCCESS:
        return (
          <IconWithText icon={IconCheckAlt} semanticColor="textPositive">
            <StyledIconText variant="viola" semanticColor="textSubdued">
              {t(
                'atlas.encoding.ingested',
                'Encoding successful',
                'Indicates delivery was fully ingested into Spotify',
              )}
            </StyledIconText>
          </IconWithText>
        );
      case TranscodingStatus.STATUS_PENDING:
        return (
          <IconWithText icon={IconRefresh}>
            <StyledIconText variant="viola" semanticColor="textSubdued">
              {t(
                'atlas.encoding.processing',
                'Encoding in progress',
                'Indicates delivery in the process of ingesting into Spotify',
              )}
            </StyledIconText>
          </IconWithText>
        );
      case TranscodingStatus.STATUS_FAILURE:
        return (
          <IconWithText icon={IconXAlt} semanticColor="textNegative">
            <StyledIconText variant="viola" semanticColor="textSubdued">
              {t(
                'atlas.encoding.failed',
                'Encoding failed',
                'Indicates delivery failed to ingest into Spotify',
              )}
            </StyledIconText>
          </IconWithText>
        );
      default:
        /*
                  per Pluto squad: "Unknown" status is a placeholder and isn't produced anywhere in Sonar UI.
                  "Unknown" status in Catalog indicates delivery is pre-2021 which Sonar's API has no record of.
                */
        return null;
    }
  };

  const transcodingStatusForAssetType = (
    assetType: AssetType,
  ): AssetTranscodingStatus | null => {
    const statusForAssetType = assetTranscodingStatuses.find(
      assetTranscodingStatus => assetTranscodingStatus.assetType === assetType,
    );
    return statusForAssetType || null;
  };

  const getWrapperForPill = (
    label: string,
    transcodingStatus: TranscodingStatus | null,
  ) => {
    return (
      <PillInfoCaseWrapper>
        <PillInfo
          label={label}
          value={
            transcodingStatus
              ? renderTranscodingStatusValue(transcodingStatus)
              : ''
          }
          tooltipMessage={undefined}
        />
      </PillInfoCaseWrapper>
    );
  };

  const renderAssetTranscodingStatuses = () => {
    // If all assets are present, all are shown with the correct statuses
    // If an asset type is missing, it isn't shown
    // If all asset types are missing, show default "This delivery has no assets"

    if (assetTranscodingStatuses.length === 0) {
      return getWrapperForPill(
        t(
          'atlas.labels.delivery_has_no_assets',
          'This delivery has no assets',
          'Label for a delivery with no assets',
        ),
        null,
      );
    }

    const audioStatus = transcodingStatusForAssetType(
      AssetType.DELIVERY_ASSET_TYPE_AUDIO,
    );
    const imageStatus = transcodingStatusForAssetType(
      AssetType.DELIVERY_ASSET_TYPE_IMAGE,
    );
    const videoStatus = transcodingStatusForAssetType(
      AssetType.DELIVERY_ASSET_TYPE_VIDEO,
    );

    return (
      <span>
        {audioStatus
          ? getWrapperForPill(
              t(
                'atlas.labels.delivery_audio_assets',
                'Audio assets',
                'Label for delivery audio assets',
              ),
              audioStatus.status,
            )
          : null}
        {imageStatus
          ? getWrapperForPill(
              t(
                'atlas.labels.delivery_image_assets',
                'Image assets',
                'Label for delivery image assets',
              ),
              imageStatus.status,
            )
          : null}
        {videoStatus
          ? getWrapperForPill(
              t(
                'atlas.labels.delivery_video_assets',
                'Video assets',
                'Label for delivery video assets',
              ),
              videoStatus.status,
            )
          : null}
      </span>
    );
  };

  const isErrorFetchingXmlData = xmlDataStatus && xmlDataStatus !== 200;
  const isDownloading =
    downloadStatus === DOWNLOAD_STATUS.DOWNLOADING && !isErrorFetchingXmlData;

  const COPY_BATCH_ID = t(
    'atlas.actions.copy_batch_id',
    'Copy Batch ID',
    'Text for Copy Batch ID action',
  );
  const COPIED_TO_CLIPBOARD = t(
    'atlas.snackbars.copied_to_clipboard',
    'Copied to clipboard',
    'Snackbar message to indicate copied to clipboard',
  );

  return (
    <TableRow>
      <StyledTableCell colSpan={7}>
        <Container>
          <Type
            as="p"
            variant="cello"
            semanticColor="textBase"
            paddingBottom={spacer24}
          >
            {t(
              'atlas.delivery_details',
              'Delivery details',
              'Title for Delivery detailssection',
            )}
          </Type>
          <PillsContainer>
            <Column marginRight>
              <PillInfoWithIcon
                label={t(
                  'atlas.labels.batch_id',
                  'Batch ID',
                  'Label for Batch ID',
                )}
                value={
                  getSonarUrlFromId ? (
                    <StyledTextLink
                      href={getSonarUrlFromId(deliveryId)}
                      onClick={() => {
                        sendEvent({
                          eventCategory:
                            EVENT_CATEGORY.RELEASE_DELIVERIES_SECTION,
                          eventAction: EVENT_ACTION.CLICK,
                          eventLabel: 'Navigated to Deliveries via Batch ID',
                          product: GA_PRODUCT_CATALOG,
                        });
                      }}
                      semanticColor="textBrightAccent"
                    >
                      {deliveryId}
                    </StyledTextLink>
                  ) : (
                    <Type variant="viola" semanticColor="textSubdued">
                      {deliveryId}
                    </Type>
                  )
                }
                icon={
                  <CopyIconContainer>
                    <CopyToGreenCheckAnimation
                      hasHoverBackground
                      label="Copy Batch ID"
                      tooltipText={COPY_BATCH_ID}
                      copyText={deliveryId}
                      snackbarText={COPIED_TO_CLIPBOARD}
                      screenReaderText={COPY_BATCH_ID}
                      handleClick={() =>
                        sendEvent({
                          eventCategory:
                            EVENT_CATEGORY.RELEASE_DELIVERIES_SECTION,
                          eventAction: EVENT_ACTION.CLICK,
                          eventLabel: 'Batch ID copied',
                          product: GA_PRODUCT_CATALOG,
                        })
                      }
                    />
                  </CopyIconContainer>
                }
                backgroundColor="backgroundHighlight"
              />
              {deliveryStatus !== DeliveryStatus.UNKNOWN
                ? renderAssetTranscodingStatuses()
                : null}
            </Column>
            <Column marginLeft>
              <PillInfoWithIcon
                label={t(
                  'atlas.labels.delivery_xml',
                  'Delivery XML',
                  `Label text for Delivery XML. Keep 'XML' as is.`,
                )}
                value={
                  <DeliveryXmlContent
                    aria-label="Open XML modal"
                    feedGid={feedGid}
                    deliveryId={deliveryId}
                    productId={productId}
                  />
                }
                icon={
                  isDownloading ? (
                    <CopyIconContainer>
                      <LoadingIndicator size="small" />
                    </CopyIconContainer>
                  ) : (
                    <StyledDownloadIcon
                      disabled={isDownloading}
                      hasHoverBackground
                      hoverText={t(
                        'atlas.actions.download_xml',
                        'Download XML',
                        `Action to download XML file. Keep the string 'XML' as is.`,
                      )}
                      screenReaderText={t(
                        'atlas.labels.delivery_xml',
                        'Delivery XML',
                        `Label text for Delivery XML. Keep 'XML' as is.`,
                      )}
                      handleClick={handleDownloadIconClick}
                    />
                  )
                }
                backgroundColor="backgroundHighlight"
              />
            </Column>
          </PillsContainer>
          {deliveryErrors && deliveryErrors.length > 0 && (
            <DeliveryErrors deliveryErrors={deliveryErrors} />
          )}
        </Container>
      </StyledTableCell>
    </TableRow>
  );
};
